/**
 * MeetingRow - Individual meeting row with expand/collapse for "Meeting prep"
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, XClose } from '@untitledui/icons';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';

import { useUser } from '@/allauth_auth/hooks';
import { usePermissions } from '@/hooks/usePermissions';
import { useRefreshAt } from '@/hooks/useRefreshAt';
import { Avatar } from '@/lib/base/avatar/avatar';
import { Button } from '@/lib/base/buttons/button';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { MeetingProviderIcon } from '@/lib/crm/components/MeetingProviderIcon';
import {
  FOCUSED_SECTION_MEETING_PREP,
  FOCUSED_SECTION_PARAM,
} from '@/lib/crm/hooks/useFocusedAssociationScroll';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import {
  allNonOrganizerGuestsDeclined,
  DECLINED_TITLE_CLASSNAME,
  formatMeetingTime,
  getGuestDisplayName,
  getMeetingDurationMinutes,
  getMeetingPlatformLabel,
  getNextJoinStateChange,
  getVideoPlatform,
  isMeetingImminent,
} from '@/lib/crm/record/utils/meetingHelpers';
import { isValidViewDefinition } from '@/lib/crm/record/utils/meetingPrepTypes';
import { getAvatarInitials, getGravatarUrl } from '@/lib/crm/utils/avatar';
import { logger } from '@/lib/logger';
import { stripEntityTags } from '@/lib/markdown/entityRendering';
import { useOrgTimeFormat } from '@/lib/time';
import { isEventPast } from '@/pages/calendar/utils/calendarUtils';
import type {
  CalendarEvent,
  CalendarEventGuest,
} from '@/pages/calendar/utils/eventMapper';
import { useHasReadableMeetingNotes } from '@/pages/dashboard/hooks/useHasReadableMeetingNotes';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';
import { cx } from '@/utils/cx';

import { useDismissMeeting } from '../hooks/useDismissMeeting';
import {
  isRecoverableError,
  useMeetingResearch,
} from '../hooks/useMeetingResearch';
import { DashboardButton } from './DashboardButton';
import {
  classifyMeetingAudience,
  type MeetingAudience,
} from './utils/classifyMeetingAudience';
import {
  createMeetingNotesQueryConfig,
  isSummaryEligibleMeeting,
  meetingNeedsSummary,
  SUMMARY_ELIGIBILITY_GRACE_MS,
} from './utils/meetingResearchQuery';

// Style constants hoisted outside component
const COLLAPSED_HEIGHT = 'h-14' as const;

// Transition style for grid animation (uses literal classes for Tailwind detection)
const TRANSITION_STYLE =
  'transition-[grid-template-rows] duration-150 ease-out' as const;
const TRANSITION_NONE = 'transition-none' as const;

/** Help text for the dashboard Dismiss control.
 *
 *  Deliberately says "the whole series if it repeats" rather than branching on
 *  recurrence: `CalendarEvent` carries no series id (see
 *  `pages/calendar/utils/eventMapper.ts`), so the row cannot tell a one-off from
 *  an occurrence without threading a new field through the calendar pipeline and
 *  the dashboard query's property list — the latter being cache-key-bound. The
 *  conditional phrasing is honest in both cases and warns about the series
 *  consequence, which is the surprising one. Mirrors the undismiss copy in
 *  `lib/crm/record/hooks/useMeetingHeaderActions.ts`. */
const DISMISS_MEETING_DESCRIPTION =
  "We'll hide this meeting from your dashboard – the whole series if it " +
  "repeats. Only you see this change; show it again from the meeting's record.";

/**
 * Extract the short `preamble` from a prep brief's `ahoy_view_definition` JSON.
 * Returns null when the prep was written by the pre-v2 codepath (no view_definition)
 * or when the JSON is malformed — callers should fall back to `ahoy_summary`.
 */
function extractPreamble(
  viewDefJson: string | undefined | null
): string | null {
  if (!viewDefJson) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(viewDefJson);
  } catch {
    return null;
  }
  if (!isValidViewDefinition(parsed)) {
    return null;
  }
  const preamble = parsed.ai_summary?.preamble;
  return preamble && preamble.trim().length > 0 ? preamble : null;
}

interface MeetingRowProps {
  /** Calendar event data */
  event: CalendarEvent;
  /** Whether this row is expanded */
  isExpanded: boolean;
  /** Callback to toggle expanded state */
  onToggle: (id: string) => void;
  /** Organization ID for API calls */
  orgId: number;
  /** Meeting entity ID from CalendarEvent.id */
  meetingEntityId: string;
  /** Deal stage entity ID. Surfaced as a screen-reader stage label; the left
   *  accent bar's color now reflects internal/external meeting audience, not stage. */
  dealStageEntityId?: string;
  /** Company entity ID (for badge) */
  companyEntityId?: string;
  /** IANA timezone for time display (e.g. "America/New_York"). Falls back to browser local time. */
  timeZone?: string;
  /** Callback to open the create-note modal with this meeting pre-associated. */
  onAddNote?: (meetingEntityId: string) => void;
}

/**
 * Format guest count as singular/plural string
 */
function formatGuestCount(count: number): string {
  return count === 1 ? '1 guest' : `${count} guests`;
}

/**
 * Left accent-bar styling by meeting audience:
 *   internal — every attendee shares the org's email domain (coworkers)
 *   external — at least one attendee is outside the org domain
 *   unknown  — can't be determined (no guests, or no usable org-domain anchor)
 *
 * Uses design-system utility tokens with dark-mode variants so the strip reads
 * correctly in both themes. (Deal-stage pipeline coloring is a separate,
 * backend-blocked effort — it needs stage.color exposed on the CalendarEvent
 * serializer — so the bar carries the internal/external signal for now.)
 */
const AUDIENCE_BAR_CLASS: Record<MeetingAudience, string> = {
  internal: 'bg-utility-success-500 dark:bg-utility-success-400',
  external: 'bg-utility-blue-500 dark:bg-utility-blue-400',
  unknown: 'bg-utility-gray-300 dark:bg-utility-gray-600',
};

/** Accessible label for internal/external bars; the unknown bar stays decorative. */
const AUDIENCE_BAR_LABEL: Record<MeetingAudience, string | undefined> = {
  internal: 'Internal meeting',
  external: 'External meeting',
  unknown: undefined,
};

/**
 * Single attendee avatar with tooltip showing name/email on hover.
 */
function AttendeeAvatar({ guest }: { guest: CalendarEventGuest }) {
  const displayName = getGuestDisplayName(guest);
  const gravatarUrl = getGravatarUrl(guest.email, 48);

  const name = guest.name || guest.email.split('@')[0];
  const title = guest.is_organizer ? `${name} (organizer)` : name;

  return (
    <Tooltip title={title} description={guest.email}>
      <TooltipTrigger>
        <Avatar
          size='xs'
          src={gravatarUrl}
          alt={displayName}
          initials={getAvatarInitials(displayName)}
        />
      </TooltipTrigger>
    </Tooltip>
  );
}

/** RSVP status groups for bucketing attendees */
const RSVP_GROUPS = [
  {
    key: 'accepted',
    label: 'Accepted',
    statuses: ['accepted'],
  },
  {
    key: 'declined',
    label: 'Declined',
    statuses: ['declined'],
  },
  {
    key: 'pending',
    label: 'Awaiting',
    statuses: ['tentative', 'needs_action', undefined],
  },
] as const;

/**
 * Render the dashboard card preview of a meeting prep brief.
 *
 * Prefers the purpose-built `preamble` (2-4 sentences, flat prose) from
 * `ahoy_view_definition` when available — it's bounded, so no clipping is
 * needed and the whole overview fits. Falls back to the legacy multi-section
 * `ahoy_summary` markdown (clipped to 4 lines) for prep briefs written before
 * the v2.0 view_definition shipped, which don't carry a preamble.
 */
function MeetingPrepPreview({
  research,
}: {
  research: CrmObject;
}): React.ReactElement {
  const preamble = extractPreamble(
    getPropertyValue<string>(research.properties?.ahoy_view_definition)
  );

  if (preamble) {
    return (
      <MarkdownContent
        content={stripEntityTags(preamble)}
        size='compact'
        className='[&>*:first-child]:mt-0 [&>*:last-child]:mb-0'
      />
    );
  }

  const summary =
    getPropertyValue<string>(research.properties?.ahoy_summary) ||
    'No summary available.';
  return (
    <div className='line-clamp-4'>
      <MarkdownContent
        content={stripEntityTags(summary)}
        size='compact'
        className='[&>*:first-child]:mt-0 [&>*:last-child]:mb-0'
      />
    </div>
  );
}

/**
 * Display meeting attendees bucketed by acceptance status.
 * Each row: "Label  [avatar] [avatar] [avatar]"
 */
function AttendeesDisplay({
  guests,
}: {
  guests?: CalendarEventGuest[];
}): React.ReactElement {
  // No attendees
  if (!guests || guests.length === 0) {
    return <div className='text-neutral-500'>No attendees</div>;
  }

  // Group guests by RSVP status
  const grouped = RSVP_GROUPS.map(group => ({
    ...group,
    items: guests.filter(guest =>
      (group.statuses as readonly (string | undefined)[]).includes(
        guest.response_status
      )
    ),
  })).filter(group => group.items.length > 0);

  return (
    <div className='space-y-2'>
      {grouped.map(group => (
        <div key={group.key} className='flex items-center gap-2'>
          <span className='text-[12px] font-medium text-neutral-500 dark:text-neutral-400 w-16 shrink-0'>
            {group.label}
          </span>
          <div className='flex items-center gap-1 flex-wrap'>
            {group.items.map((guest, idx) => (
              <AttendeeAvatar key={guest.email || idx} guest={guest} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * MeetingRow component - wrapped in React.memo for performance
 */
export const MeetingRow = React.memo(function MeetingRow({
  event,
  isExpanded,
  onToggle,
  orgId,
  meetingEntityId,
  dealStageEntityId,
  companyEntityId: _companyEntityId,
  timeZone,
  onAddNote,
}: MeetingRowProps) {
  const queryClient = useQueryClient();
  const { timeFormat } = useOrgTimeFormat();
  const { openSlideout } = useSlideoutStack();
  const expandedContentRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const wasKeyboardTriggered = useRef(false);
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Schedule a re-render when the join button state should change
  // (appears 15min before start, disappears at end). The re-render
  // recalculates the non-memoized `isJoinable` check below.
  useRefreshAt(getNextJoinStateChange(event.start_time, event.end_time));

  // Refresh when a past meeting crosses the 1-hour-ended mark so
  // showNoSummaryWarning recomputes. showNoSummaryWarning is intentionally
  // not memoized (like isJoinable below) because it reads Date.now() and
  // must recompute on every render triggered by this timer.
  useRefreshAt(
    event.end_time
      ? new Date(event.end_time).getTime() + SUMMARY_ELIGIBILITY_GRACE_MS
      : null
  );

  // Research data (prep briefs) — fetch when expanded, plus hover-intent
  // prefetch and error-observability logging. See useMeetingResearch for why
  // these are grouped together.
  const { data, isLoading, error, prefetchHandlers } = useMeetingResearch(
    orgId,
    meetingEntityId,
    isExpanded
  );

  // Past-ness is derived from end_time via the shared isEventPast helper —
  // computed here (rather than only near the CTA logic below) because
  // isSummaryCandidate needs it immediately below.
  const hasEnded = isEventPast(event);

  // Whether this row is eligible for the "no summary" banner at all: a past
  // meeting, over for more than the grace period, with more than one guest.
  // Not memoized (like isJoinable) — reads Date.now(), so it must recompute on
  // every render, including the one the 1-hour useRefreshAt above schedules.
  const isSummaryCandidate =
    hasEnded &&
    isSummaryEligibleMeeting({
      endTime: event.end_time,
      isAllDay: event.is_all_day,
      guestCount: event.guests?.length ?? 0,
      referenceMs: Date.now(),
    });

  // Fetch captured meeting notes. Deliberately NOT gated on `isExpanded` the
  // way the research query above is: the banner renders only while the row is
  // collapsed, so an expansion-gated fetch left it unreachable until a user
  // expanded a row and collapsed it again. Gating on `isSummaryCandidate`
  // instead keeps the fetch bounded to rows that could actually show it.
  const { data: notesData, error: notesError } = useQuery({
    ...createMeetingNotesQueryConfig(orgId, meetingEntityId),
    enabled: !!meetingEntityId && (isExpanded || isSummaryCandidate),
    retry: (failureCount, error) =>
      failureCount < 3 && isRecoverableError(error),
  });

  // Log notes-query errors for observability, mirroring the research-error
  // logging effect inside useMeetingResearch.
  useEffect(() => {
    if (notesError) {
      const normalizedError =
        notesError instanceof Error
          ? notesError
          : new Error(String(notesError));
      logger.error('Failed to fetch meeting notes', normalizedError, {
        component: 'MeetingRow',
        meetingEntityId,
        orgId,
        originalErrorType: notesError?.constructor?.name || typeof notesError,
        originalError: String(notesError),
      });
    }
  }, [notesError, meetingEntityId, orgId]);

  // Toggle handler
  const handleToggle = useCallback(() => {
    onToggle(event.id);
  }, [onToggle, event.id]);

  // Focus management on collapse - restore focus to toggle button after isExpanded updates
  useEffect(() => {
    if (!isExpanded && wasKeyboardTriggered.current) {
      // Clear any pending expand-focus timeout to prevent focus moving into collapsed content
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
        focusTimeoutRef.current = null;
      }
      requestAnimationFrame(() => {
        toggleButtonRef.current?.focus();
      });
      wasKeyboardTriggered.current = false;
    }
  }, [isExpanded]);

  // Cleanup focus timeout on unmount only (separate effect to avoid clearing on expand)
  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
        focusTimeoutRef.current = null;
      }
    };
  }, []);

  // Keyboard handler - tracks keyboard activation for focus management
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        wasKeyboardTriggered.current = true;
        // On expand via keyboard, move focus into expanded content
        if (!isExpanded) {
          focusTimeoutRef.current = setTimeout(() => {
            expandedContentRef.current?.focus();
            focusTimeoutRef.current = null;
          }, 250); // Wait for animation
        }
      }
    },
    [isExpanded]
  );

  // Handle view full prep button click. Prep sits below the whole properties
  // block in the meeting slideout, so ask the slideout to scroll it into view.
  //
  // This is a one-shot instruction, not view state: MeetingDetailSection's
  // useFocusedSectionScroll deletes the param as soon as it acts (or decides
  // there's nothing to act on), so it never lingers in the URL after the
  // slideout closes.
  const handleViewPrep = useCallback(() => {
    openSlideout(meetingEntityId, {
      searchParams: { [FOCUSED_SECTION_PARAM]: FOCUSED_SECTION_MEETING_PREP },
    });
  }, [openSlideout, meetingEntityId]);

  // Handle view meeting details button click (no prep available)
  const handleViewMeetingDetails = useCallback(() => {
    openSlideout(meetingEntityId);
  }, [openSlideout, meetingEntityId]);

  // Handle "Add note" button click — opens the NoteCreateModal with
  // this meeting pre-associated.
  const handleAddNote = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onAddNote?.(meetingEntityId);
    },
    [onAddNote, meetingEntityId]
  );

  const { hasPermission } = usePermissions();
  const { dismissMeeting, isDismissing } = useDismissMeeting(orgId);
  // "You may hide what you may see": gates on CRM read access rather than
  // 'ahoy_property_updated' (system-gated, would block all users) because the
  // dismiss write goes through the hidden-meeting store, not the CRM property
  // layer. This permission no longer governs the actual write.
  const canDismiss = hasPermission('ahoy_meeting');
  const isDismissingThisMeeting = isDismissing(meetingEntityId);
  const handleDismiss = useCallback(async () => {
    try {
      await dismissMeeting(meetingEntityId);
    } catch (error) {
      logger.error(
        'Failed to dismiss meeting',
        error instanceof Error ? error : undefined,
        { meetingEntityId, orgId }
      );
      toast.error("We couldn't dismiss this meeting. Try again.");
    }
  }, [dismissMeeting, meetingEntityId, orgId]);

  // Classify the meeting as internal / external / unknown from its attendees'
  // email domains, anchored on the current user's email (the org domain). Drives
  // the left accent-bar color so users can tell internal from external at a
  // glance. Falls back to a neutral bar when it can't be determined.
  const currentUser = useUser();
  const currentUserEmail = currentUser?.email as string | undefined;
  const audience = useMemo(
    () => classifyMeetingAudience(event.guests, currentUserEmail),
    [event.guests, currentUserEmail]
  );
  const audienceLabel = AUDIENCE_BAR_LABEL[audience];

  const startTime = formatMeetingTime(event.start_time, timeZone, timeFormat);
  const durationMinutes = useMemo(
    () => getMeetingDurationMinutes(event.start_time, event.end_time),
    [event.start_time, event.end_time]
  );
  const videoPlatform = useMemo(
    () => getVideoPlatform(event.meet_link),
    [event.meet_link]
  );
  const videoPlatformLabel = useMemo(
    () => getMeetingPlatformLabel(event.meet_link, event.meet_provider),
    [event.meet_link, event.meet_provider]
  );

  // Show Join whenever the meeting has a valid video link and has not yet
  // ended. The end boundary uses a strict `end > Date.now()` comparison so Join
  // disappears at the exact end tick — the same boundary the prominence logic
  // uses (`isMeetingImminent` is `now < end`). The finite-end guard preserves
  // the old "no button" behavior for events with a missing/invalid end_time (an
  // unparseable end_time is not finite, so it never counts as joinable).
  // Not memoized (reads Date.now()); the useRefreshAt above re-renders at the
  // end boundary so this flips at the right moment.
  const endTimeMs = new Date(event.end_time).getTime();
  const canJoin = !!(
    videoPlatform &&
    event.meet_link &&
    Number.isFinite(endTimeMs) &&
    endTimeMs > Date.now()
  );
  // Prominence only: imminent (within 15 min) or in progress -> primary; else secondary.
  // Not memoized (reads Date.now() via isMeetingImminent); the useRefreshAt below
  // re-renders at the window boundary so this flips at the right moment.
  const joinIsImminent = isMeetingImminent(event.start_time, event.end_time);

  // De-emphasize meetings where every non-organizer guest declined.
  // Google still treats these as "confirmed" (the organizer hasn't
  // cancelled), so they slip past `ahoy_status != cancelled`. Render them
  // with a strikethrough title so the user sees they're effectively dead;
  // matches the visual treatment on the full /meetings calendar.
  const isAllDeclined = useMemo(
    () => allNonOrganizerGuestsDeclined(event.guests),
    [event.guests]
  );

  // Handle join button click
  const handleJoin = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (event.meet_link) {
        try {
          const url = new URL(event.meet_link);
          if (url.protocol === 'https:' || url.protocol === 'http:') {
            window.open(event.meet_link, '_blank', 'noopener,noreferrer');
          }
        } catch {
          // Invalid URL — do nothing
        }
      }
    },
    [event.meet_link]
  );

  // The action CTA follows the meeting's own clock: prep is the useful thing
  // until the meeting ends, the summary afterward. `hasEnded` is computed
  // earlier (needed by isSummaryCandidate above).
  //
  // Live, store-backed rather than the map-time event.hasSummary snapshot, so a
  // summary that lands over the WebSocket while the dashboard is open flips the
  // CTA without a reload. Falls back to the mapped value when the meeting isn't
  // in the store.
  const hasSummary = useHasReadableMeetingNotes(
    meetingEntityId,
    event.hasSummary ?? false
  );
  const hasPrep = !!data && data.length > 0;

  // Each branch only offers what the user can actually open: prep when prep
  // exists, the summary when a readable one exists, and the plain record
  // otherwise — never a CTA that lands on an empty section.
  const showPrepCta = !hasEnded && hasPrep;

  let ctaLabel: string;
  if (showPrepCta) {
    ctaLabel = 'View full prep';
  } else if (hasEnded && hasSummary) {
    ctaLabel = 'View meeting summary';
  } else {
    ctaLabel = 'View meeting details';
  }

  // Only the prep CTA deep-links to a section. Once the meeting has ended the
  // slideout already renders "Meeting summaries" above prep on its own (see
  // meeting-detail-section.tsx isMeetingPast branch), so the plain slideout
  // lands in the right place for both past branches.
  const handleCtaClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (showPrepCta) {
        handleViewPrep();
      } else {
        handleViewMeetingDetails();
      }
    },
    [showPrepCta, handleViewPrep, handleViewMeetingDetails]
  );

  // Warning: past meeting with no summary on record and multiple attendees.
  // meetingNeedsSummary treats an unresolved notes query as "don't claim", so
  // the banner only appears once the fetch has actually come back empty.
  const showNoSummaryWarning =
    isSummaryCandidate && meetingNeedsSummary(notesData);

  // Detect reduced motion preference
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const transitionStyle = prefersReducedMotion
    ? TRANSITION_NONE
    : TRANSITION_STYLE;

  return (
    <article
      className={cx(
        'group relative overflow-hidden bg-white dark:bg-neutral-900',
        // Use the design-system border token (visible, theme-aware) instead of
        // a ~4% black hairline that disappeared against the dashboard's light
        // gradient background. Strengthens to border-primary on hover.
        'rounded-[4px] border border-secondary hover:border-primary transition-colors',
        showNoSummaryWarning &&
          'border-warning-300 dark:border-warning-700 opacity-100'
      )}
      {...prefetchHandlers}
    >
      {/* Left accent bar (12px wide, rounded on left side only). Color encodes
          internal vs external meeting audience; carries an accessible label
          (and native tooltip) for the internal/external states. */}
      <div
        className={cx(
          'absolute left-0 top-0 bottom-0 w-[12px] rounded-bl-[3px] rounded-tl-[3px] opacity-70',
          AUDIENCE_BAR_CLASS[audience]
        )}
        data-audience={audience}
        role={audienceLabel ? 'img' : undefined}
        aria-label={audienceLabel}
        title={audienceLabel}
        aria-hidden={audienceLabel ? undefined : true}
      />

      {/* Collapsed content header - toggle and actions as sibling controls */}
      <div
        className={cx(
          COLLAPSED_HEIGHT,
          'w-full flex items-center gap-3 px-4 pl-[28px]'
        )}
      >
        {/* Toggle button - covers title, time, and chevron */}
        <button
          ref={toggleButtonRef}
          type='button'
          className={cx(
            'flex items-center gap-3 flex-1 min-w-0',
            'cursor-pointer',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-focus-ring focus-visible:ring-inset rounded-sm'
          )}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          aria-expanded={isExpanded}
          aria-controls={`meeting-${event.id}-content`}
        >
          {/* Meeting title + duration + video platform icon */}
          <div className='flex items-center gap-2 flex-1 min-w-0'>
            <span
              className={cx(
                'text-[14px] font-medium text-neutral-900 dark:text-neutral-100 truncate',
                isAllDeclined && DECLINED_TITLE_CLASSNAME
              )}
            >
              {event.title}
            </span>
            {durationMinutes !== null && (
              <span className='hidden sm:inline text-[14px] text-neutral-500 dark:text-neutral-400 shrink-0'>
                {durationMinutes} min
              </span>
            )}
            <MeetingProviderIcon
              meetLink={event.meet_link}
              providerName={event.meet_provider}
              providerIconUrl={event.meet_provider_icon_url}
              className='size-4'
            />
          </div>

          {/* Company badge (if available) */}
          {/* TODO: Add company badge once we have association data */}

          {/* Guest count badge */}
          {event.guests && event.guests.length > 0 && (
            <span className='hidden sm:inline text-[13px] text-neutral-500 dark:text-neutral-400 shrink-0'>
              {formatGuestCount(event.guests.length)}
            </span>
          )}

          {/* Screen reader stage label */}
          {dealStageEntityId && (
            <span className='sr-only'>Stage: {dealStageEntityId}</span>
          )}
        </button>

        {/* Join button - to the left of time */}
        {canJoin && (
          <DashboardButton
            variant={joinIsImminent ? 'primary' : 'secondary'}
            onClick={handleJoin}
            aria-label={`Join ${event.title} on ${videoPlatformLabel}`}
          >
            Join
          </DashboardButton>
        )}

        {/* Time */}
        <div className='text-[14px] font-semibold text-neutral-900 dark:text-neutral-100 shrink-0'>
          {startTime}
        </div>

        {/* Dismiss — icon-only with a hover tooltip.
            The label lived here as text until it was found to be squeezing the meeting
            title (the title sits in a `min-w-0 … truncate` box, so every fixed-width
            sibling eats into it). An X costs ~a third of the width.

            The meaning is carried two ways so nothing depends on hover: the tooltip for
            sighted pointer users, and `aria-label` for screen readers and voice control.
            The button stays permanently mounted and visible — a hover-gated control
            would be absent from the accessibility tree while idle, and so unreachable in
            screen-reader browse mode and by voice ("click Dismiss"). */}
        {canDismiss && (
          <Tooltip
            title='Dismiss'
            description={DISMISS_MEETING_DESCRIPTION}
            placement='top'
          >
            <Button
              color='tertiary'
              size='sm'
              iconLeading={XClose}
              aria-label='Dismiss meeting'
              onClick={handleDismiss}
              isDisabled={isDismissingThisMeeting}
              className='shrink-0'
            />
          </Tooltip>
        )}

        {/* Expand/collapse icon */}
        <button
          type='button'
          onClick={handleToggle}
          className='shrink-0 text-neutral-400 dark:text-neutral-500 p-1 -mr-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring'
          aria-label={
            isExpanded ? 'Collapse meeting details' : 'Expand meeting details'
          }
        >
          {isExpanded ? (
            <ChevronUp className='size-4' aria-hidden='true' />
          ) : (
            <ChevronDown className='size-4' aria-hidden='true' />
          )}
        </button>
      </div>

      {/* Warning banner for past meetings with no summary */}
      {showNoSummaryWarning && !isExpanded && (
        <div className='flex items-center gap-2 px-4 pl-[28px] py-2 bg-warning-50 dark:bg-warning-950 border-t border-warning-200 dark:border-warning-800'>
          <span className='text-[12px] text-warning-700 dark:text-warning-300 flex-1'>
            No meeting summary yet
          </span>
          {onAddNote && (
            <DashboardButton variant='secondary' onClick={handleAddNote}>
              Add summary
            </DashboardButton>
          )}
        </div>
      )}

      {/* Expanded content - GPU-accelerated grid animation */}
      <div
        className={cx(
          'grid overflow-hidden',
          transitionStyle,
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
        id={`meeting-${event.id}-content`}
        aria-hidden={!isExpanded}
        inert={!isExpanded ? true : undefined}
      >
        <div className='overflow-hidden'>
          <div
            ref={expandedContentRef}
            role='region'
            aria-label='Meeting details and Ahoy analysis'
            tabIndex={-1}
            className='pr-4 pb-3 pl-[28px] space-y-3'
          >
            {/* Mobile-only: show duration and guest count that are hidden in collapsed header */}
            {isExpanded &&
              (durationMinutes !== null ||
                (event.guests && event.guests.length > 0)) && (
                <div className='sm:hidden flex items-center gap-1.5 text-[13px] text-neutral-500 dark:text-neutral-400'>
                  {durationMinutes !== null && (
                    <span>{durationMinutes} min</span>
                  )}
                  {durationMinutes !== null &&
                    event.guests &&
                    event.guests.length > 0 && (
                      <span aria-hidden='true'>·</span>
                    )}
                  {event.guests && event.guests.length > 0 && (
                    <span>{formatGuestCount(event.guests.length)}</span>
                  )}
                </div>
              )}
            {/* Content section - shows "Meeting prep" when research exists, "Attendees" otherwise */}
            <div className='-mt-1 space-y-2'>
              {/* Dynamic header based on content */}
              {!isLoading && !error && data && data.length === 0 ? (
                <div className='text-[14px] font-semibold text-[var(--color-fg-brand-primary)]'>
                  Attendees
                </div>
              ) : (
                <div className='text-[14px] font-semibold text-[var(--color-fg-brand-primary)]'>
                  Meeting prep
                </div>
              )}
              <div className='text-[14px] text-neutral-500 dark:text-neutral-400 leading-[21px]'>
                {isLoading && <div>Loading…</div>}
                {!!error && !isLoading && (
                  <div className='space-y-2'>
                    <div className='text-error-600 dark:text-error-400'>
                      {isRecoverableError(error)
                        ? 'Unable to load research. Please try again.'
                        : 'Research unavailable.'}
                    </div>
                    {isRecoverableError(error) && (
                      <button
                        type='button'
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          queryClient.invalidateQueries({
                            queryKey: [
                              'meetingResearch',
                              orgId,
                              meetingEntityId,
                            ],
                          });
                        }}
                        className='w-full py-2 px-4 text-[14px] font-semibold text-neutral-900 dark:text-neutral-100 border border-[rgba(0,0,0,0.13)] dark:border-neutral-700 rounded-[8px] hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors'
                      >
                        Retry
                      </button>
                    )}
                  </div>
                )}
                {!isLoading && !error && data && data.length === 0 && (
                  <AttendeesDisplay guests={event.guests} />
                )}
                {!isLoading && !error && data && data.length > 0 && (
                  <MeetingPrepPreview research={data[0]} />
                )}
              </div>
            </div>

            {/* Action buttons row */}
            {!isLoading && (
              <div className='flex items-center gap-2'>
                <DashboardButton variant='secondary' onClick={handleCtaClick}>
                  {ctaLabel}
                </DashboardButton>
                {onAddNote && (
                  <DashboardButton variant='secondary' onClick={handleAddNote}>
                    Add note
                  </DashboardButton>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </article>
  );
});
